import { CircleIcon, SortIcon } from '@assets/icons';
import { Group } from '@components/layout';
import { Box, Divider, Menu, MenuItem, useTheme } from '@mui/material';
import { useRef, useState } from 'react';

type SortButtonProps = {
  sort: ['asc' | 'desc', string];
  variant: 'primary' | 'secondary';
  options: string[];
  onSortValueChange: (value: string) => void;
  onSortDirectionChange: (direction: 'asc' | 'desc') => void;
};

export const SortButton = ({
  sort,
  variant,
  options,
  onSortValueChange,
  onSortDirectionChange,
}: SortButtonProps) => {
  const { palette } = useTheme();

  const anchorEl = useRef<HTMLDivElement>(null);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  const handleSortValueChange = (value: string) => {
    onSortValueChange(value);
  };

  const handleSortDirectionChange = (direction: 'asc' | 'desc') => {
    onSortDirectionChange(direction);
  };

  return (
    <>
      <Box
        ref={anchorEl}
        py={1}
        px={1.5}
        display="flex"
        alignItems="center"
        onClick={() => {
          setSortMenuOpen(true);
        }}
        style={{ cursor: 'pointer', outline: 'none' }}
        border="1px solid"
        borderColor={palette.table.modeButton.border.selected}
        sx={{
          borderRadius: '0 3px 3px 0',
          backgroundColor:
            variant === 'primary' ? palette.background.paper : palette.input.background.default,
          borderLeftWidth: 0,
          '& .deckard-icon': {
            color: palette.table.modeButton.icon.default,
          },

          '&:hover': {
            backgroundColor: palette.table.modeButton.background.hover,
            '& .deckard-icon': {
              color: palette.table.modeButton.icon.hover,
            },
          },
          '&:active': {
            backgroundColor: palette.table.modeButton.background.selected,
          },
        }}
      >
        <SortIcon />
      </Box>
      <Menu
        anchorEl={anchorEl.current}
        open={sortMenuOpen}
        onClose={() => {
          setSortMenuOpen(false);
        }}
        disablePortal
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => handleSortDirectionChange('asc')}>
          <SortMenuOption selected={sort?.[0] === 'asc'} label="Ascending" />
        </MenuItem>
        <MenuItem onClick={() => handleSortDirectionChange('desc')}>
          <SortMenuOption selected={sort?.[0] === 'desc'} label="Descending" />
        </MenuItem>
        <Divider />
        {options.map((option) => (
          <MenuItem key={option} onClick={() => handleSortValueChange(option)}>
            <SortMenuOption selected={sort?.[1] === option} label={option} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const SortMenuOption = ({ selected, label }: { selected: boolean; label: string }) => {
  const { palette } = useTheme();
  return (
    <Group alignItems="center" gap={2}>
      {selected ? (
        <CircleIcon
          size={7}
          color={palette.mode === 'dark' ? palette.primary.main : palette.primary.dark}
        />
      ) : (
        <Box width={7} />
      )}
      {label}
    </Group>
  );
};
